import React, { Fragment, useState } from 'react';
import { useAsync } from 'react-async';
import { getMyProfile, updateMyProfile } from '../../api/services/meService';
import { useAppDispatch } from '../../app/hooks';
import { ResponseStatus } from '../../components/notifications';
import { FieldInput } from '../../components/fieldInput';
import Layout from '../../components/layout';
import LayoutError from '../../components/layoutError';
import SubmitButton from '../../components/submitButton';
import { updateProfile } from '../../slices/userSlice';

const MyProfile = () => {
  const dispatch = useAppDispatch();

  // State
  const [firstName, setFirstName] = useState('');
  const [middleInitial, setMiddleInitial] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');

  // Data
  const me = useAsync({
    promiseFn: getMyProfile,
    onResolve: (r) => {
      setFirstName(r.data.user.firstName);
      setMiddleInitial(r.data.user.middleInitial ?? '');
      setLastName(r.data.user.lastName);
      setEmail(r.data.user.emailAddress);
    },
  });

  const save = useAsync({
    deferFn: async () => {
      return await updateMyProfile({
        firstName: firstName,
        middleInitial: middleInitial,
        lastName: lastName,
        emailAddress: email,
        isDisabled: false,
        userRoles: [],
      });
    },
    onResolve: (r) => {
      dispatch(
        updateProfile({
          firstName: firstName,
          middleInitial: middleInitial,
          lastName: lastName,
          email: email,
          jwt: r.data.token,
        })
      );
    },
  });

  // Handlers
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save.run();
  };

  if (me.error) return <LayoutError />;

  const isValid =
    firstName.length > 0 && lastName.length > 0 && email.length > 0;

  return (
    <Layout isLoading={me.isPending}>
      <Fragment>
        <ResponseStatus
          state={save}
          successMessage='Your profile was updated successfully!'
        />
        <form onSubmit={handleSubmit}>
          <div className='grid grid-cols-6 gap-6 mb-8'>
            <div className='col-span-2 sm:col-span-2'>
              <FieldInput
                label='First Name'
                name='first-name'
                minLength={2}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>

            <div className='col-span-1 sm:col-span-1'>
              <FieldInput
                label='Middle Initial'
                name='middleInitial'
                maxLength={1}
                value={middleInitial}
                onChange={(e) => setMiddleInitial(e.target.value)}
              />
            </div>

            <div className='col-span-2 sm:col-span-2'>
              <FieldInput
                label='Last Name'
                name='last-name'
                minLength={2}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>

            <div className='col-span-6 sm:col-span-6'>
              <FieldInput
                label='Email Address'
                name='email-address'
                minLength={2}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>
          <div className='text-right'>
            <SubmitButton
              text='Save Changes'
              disabled={!isValid}
              spinning={save.isPending}
            />
          </div>
        </form>
      </Fragment>
    </Layout>
  );
};

export default MyProfile;
